import { useIntersection } from 'hooks/useIntersection';
import type {
    RenderItemProps,
    PopoverPropOffset,
    GetOptionPropsResult,
    OptionForCreate,
    OptionProps,
    PropsWithJsxAttributes,
    PropSize,
    SelectAllItem,
    SelectDropdownPropForm,
} from 'uikit';
import {
    IconSearch,
    TextField,
    Popover,
    isOptionForCreate,
    cnMixSpace,
    cnListBox,
    ListItem,
    mapVerticalSpase,
    cnConsta as cn,
    SelectLoader,
} from 'uikit';

import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { TextFieldPropOnChange } from '../../FieldNumberFormat';

import { SearchableSelectDropdownItem } from './SearchableSelectDropdownItem';
import { MemoizedSearchableSelectDropdownItemsPage } from './SearchableSelectDropdownItemsPage';

import Style from './SearchableSelectDropdown.module.css';

type Props<ITEM, GROUP> = PropsWithJsxAttributes<{
    size: PropSize;
    controlRef: React.MutableRefObject<HTMLDivElement | null>;
    dropdownRef: React.Ref<HTMLDivElement>;
    getOptionProps(props?: OptionProps<ITEM>): GetOptionPropsResult;
    form: SelectDropdownPropForm;
    isOpen: boolean;
    offset?: PopoverPropOffset | 'none';
    isLoading?: boolean;
    renderItem: (props: RenderItemProps<ITEM>) => JSX.Element | null;
    visibleItems: (
        | OptionForCreate
        | {
              items: Array<SelectAllItem | ITEM>;
              key: string | number;
              group?: GROUP;
              groupIndex: number;
              checkedCount?: number;
              totalCount?: number;
          }
    )[];
    getGroupLabel?: (group: GROUP) => string;
    labelForCreate?: string;
    labelForNotFound?: string;
    labelForEmptyItems?: string;
    notFound?: boolean;
    hasItems?: boolean;
    searchInputRef: React.RefObject<HTMLInputElement>;
    onSearch?: TextFieldPropOnChange;
    searchValue: string | null;
    isLazyLoadEnabled?: boolean;
    onNextPageRequest?: () => void;
    hasNextPage?: boolean;
    items: ITEM[];
}>;

type TSearchableSelectDropdown = <ITEM, GROUP>(props: Props<ITEM, GROUP>) => React.ReactElement | null;

const cnSelectDropdown = cn('SelectDropdown');

export const SearchableSelectDropdown: TSearchableSelectDropdown = (props) => {
    const {
        controlRef,
        size,
        getOptionProps,
        dropdownRef,
        labelForCreate,
        className,
        labelForNotFound,
        labelForEmptyItems,
        hasItems = true,
        form,
        isOpen,
        offset: offsetProp = 'none',
        renderItem,
        visibleItems,
        isLoading,
        getGroupLabel,
        notFound,
        searchInputRef,
        onSearch,
        searchValue,
        isLazyLoadEnabled,
        onNextPageRequest,
        hasNextPage,
        items,
        ...otherProps
    } = props;

    const indent = useMemo(() => (form === 'round' ? 'increased' : 'normal'), [form]);
    const offset = useMemo(() => (offsetProp === 'none' ? undefined : offsetProp), [offsetProp]);
    const optionsStyle = useMemo(() => ({ marginTop: `calc(var(--graphics-size-${size}) + 24px)` }), [size]);

    const [currentPage, setCurrentPage] = useState(1);
    const [pagesData, setPagesData] = useState<Record<string, any[]>>({});

    const isListShowed = useMemo(
        () =>
            visibleItems.filter(
                (group) => isOptionForCreate(group) || (Array.isArray(group.items) && group.items.length > 0),
            ).length > 0,
        [visibleItems],
    );

    const popoverRef = useRef<HTMLDivElement>(null);
    const optionsRef = useRef<HTMLDivElement>(null);

    const onIntersection = useCallback(
        (observer: IntersectionObserver | null) => {
            if (!searchValue && !isLoading && hasNextPage) {
                observer?.disconnect();
                setCurrentPage(currentPage + 1);
                onNextPageRequest?.();
            }
        },
        [currentPage, hasNextPage, isLoading, onNextPageRequest, searchValue],
    );

    const { refCallback, observer } = useIntersection({
        onIntersection,
    });

    const onSearchLocal: TextFieldPropOnChange = useCallback(
        (...p) => {
            observer.current?.disconnect();
            setCurrentPage(1);
            setPagesData({});
            onSearch?.(...p);
        },
        [observer, onSearch],
    );

    useEffect(() => {
        if (isLazyLoadEnabled) {
            setPagesData((prev) => ({ ...prev, [currentPage]: visibleItems }));
        }
    }, [currentPage, isLazyLoadEnabled, visibleItems]);

    useEffect(() => {
        if (isLazyLoadEnabled) {
            onNextPageRequest?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLazyLoadEnabled]);

    const pagesKeys = useMemo(() => Object.keys(pagesData).map((k) => Number(k)), [pagesData]);

    return (
        <div>
            {isOpen && (
                <Popover
                    {...otherProps}
                    anchorRef={controlRef}
                    direction="downStartLeft"
                    possibleDirections={['downStartLeft', 'upStartLeft', 'downStartRight', 'upStartRight']}
                    offset={offset}
                    ref={popoverRef}
                    className={cnSelectDropdown(null, [
                        cnListBox({ size, form, border: true, shadow: true }),
                        className,
                    ])}
                    equalAnchorWidth
                >
                    <div
                        role="listbox"
                        className={cnSelectDropdown('List', [
                            cnMixSpace({
                                pV: mapVerticalSpase[size],
                            }),
                        ])}
                        ref={dropdownRef}
                    >
                        <TextField
                            size={size}
                            inputRef={searchInputRef}
                            value={searchValue}
                            className={Style.searchableSelectInput}
                            leftSide={IconSearch}
                            onChange={onSearchLocal}
                            {...getOptionProps()}
                        />
                        <div style={optionsStyle} ref={optionsRef}>
                            {isLoading && <SelectLoader mode={isListShowed ? 'blur' : 'empty'} />}
                            {isLazyLoadEnabled &&
                                !searchValue &&
                                pagesKeys.map((key) => (
                                    <MemoizedSearchableSelectDropdownItemsPage
                                        key={key}
                                        size={size}
                                        renderItem={renderItem}
                                        getOptionProps={getOptionProps}
                                        getGroupLabel={getGroupLabel}
                                        labelForCreate={labelForCreate}
                                        visibleItems={pagesData[key]}
                                        indent={indent}
                                        isLastPage={!searchValue && key === Math.max(...pagesKeys)}
                                        lastPageRef={refCallback}
                                    />
                                ))}
                            {(!isLazyLoadEnabled || searchValue) &&
                                visibleItems.map((group) => (
                                    <SearchableSelectDropdownItem
                                        key={(group as any).key}
                                        group={group}
                                        indent={indent}
                                        size={size}
                                        renderItem={renderItem}
                                        getOptionProps={getOptionProps}
                                        getGroupLabel={getGroupLabel}
                                        labelForCreate={labelForCreate}
                                    />
                                ))}
                            {!isLoading && hasItems && notFound && labelForNotFound && (
                                <ListItem size={size} label={labelForNotFound} innerOffset={indent}>
                                    {labelForNotFound}
                                </ListItem>
                            )}
                            {!isLoading && !hasItems && labelForEmptyItems && (
                                <ListItem size={size} label={labelForEmptyItems} innerOffset={indent}>
                                    {labelForEmptyItems}
                                </ListItem>
                            )}
                        </div>
                    </div>
                </Popover>
            )}
        </div>
    );
};
